<script setup lang="ts">
import { computed } from 'vue'

import type { ComponentNode, PlanSelectionOption } from '@api/types.gen'

import TipTap from '@render/lib/TipTap'
import RenderSlot from '@render/RenderSlot.vue'
import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'

import type { OfferCardCss, OfferCardData } from './cardStyles'

const props = defineProps<{
  option: PlanSelectionOption
  data: OfferCardData
  css: OfferCardCss
  isActive: boolean
  isBestDeal: boolean
  isExitIntent: boolean
  showBestDealHeader: boolean
  showPreviousPrice: boolean
  badgeTurnedOn: boolean
  showBadge: boolean
  previewClass: string
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()

const emit = defineEmits<{ (e: 'select'): void }>()

// Keyboard activation, guarded to the card itself. The price/note slots take
// arbitrary authored components (and TipTap in the builder), so the previous
// `@keydown.enter.prevent` / `@keydown.space.prevent` bindings fired for
// descendants too — swallowing a space typed into a slot AND selecting the card.
function onKeydown(event: KeyboardEvent) {
  if (event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  emit('select')
}

const pipe = usePipe()
const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// Aliased for `v-bind()` below — scoped styles only reach this component's own
// DOM, so the resolved values have to be re-bound here rather than in the host.
const c = computed(() => props.css)

// One provider per card, so a TipTap in this card's slots resolves `comp.*`
// against THIS offer. Vue's nearest-provider semantics do the per-instance
// scoping that a single provide() on the host could not. `componentType` is the
// host's registry key — that is where `scopedVariables` metadata lives, and it
// is what the interpolation picker looks up.
useComponentScopedVariables('OfferSelectV2', () => ({
  price: props.data.price,
  oldPrice: props.data.oldPrice,
  previousPrice: props.data.previousPrice,
  periodPrice: props.data.periodPrice,
  savedAmount: props.data.savedAmount,
  discountPercent: props.data.discountPercent,
  periodCount: props.data.periodCount,
  periodUnit: props.data.periodUnit,
  isExitIntent: props.isExitIntent,
  isSelected: props.isActive,
}))

const priceSlotName = computed(() => `${props.option.id}:price`)
const noteSlotName = computed(() => `${props.option.id}:note`)

const priceChildren = computed(() => props.slotChildren?.[priceSlotName.value])
const noteChildren = computed(() => props.slotChildren?.[noteSlotName.value])

// The note row collapses entirely when nothing is authored for it, so a plan
// with no note (the 1-month card before exit intent) adds no blank line.
const hasNoteRow = computed(() => isEditor.value || (noteChildren.value?.length ?? 0) > 0)

// `previousPrice` is the pre-exit-intent price struck through beside the
// original — only meaningful once the exit offer is in play, and only when the
// two actually differ (an offer with no `exit` variant would print it twice).
const showPrevious = computed(
  () =>
    props.isExitIntent &&
    props.showPreviousPrice &&
    !!props.data.previousPrice &&
    props.data.previousPrice !== props.data.oldPrice,
)
</script>

<template>
  <!-- A `role=radio` div, deliberately NOT a <button>: the price and note slots
       accept arbitrary components, and an author dropping a Button or CTA in one
       would nest interactive elements. The HTML parser auto-closes the outer
       button on the way in, which shows up as a Nuxt hydration mismatch in the
       built pipe rather than as anything visible in the editor.

       `is-exit` carries no scoped rule of its own — it is an unhashed hook the
       stylesheet's baseCss can target for anything the exit-intent override
       fields don't cover. -->
  <div
    class="offer2-option"
    :data-part="option.id"
    role="radio"
    :aria-checked="isActive"
    tabindex="0"
    :class="[{ active: isActive, 'is-exit': isExitIntent }, previewClass]"
    @click="emit('select')"
    @keydown="onKeydown"
  >
    <div class="offer2-best-deal" v-if="isBestDeal && showBestDealHeader">
      <span class="offer2-tiptap-line">
        <slot name="best-deal" />
      </span>
    </div>

    <div class="offer2-content">
      <!-- Radio indicator -->
      <div class="offer2-check">
        <div class="offer2-check-inactive" v-if="!isActive"></div>
        <div class="offer2-check-active" v-else>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12" cy="12" r="12" />
            <circle cx="12" cy="12" r="6" />
          </svg>
        </div>
      </div>

      <div class="offer2-info">
        <div class="offer2-title">
          <TipTap
            :model-value="pipe.getTranslation(option.label.key)"
            @update:model-value="(val) => pipe.setTranslation(option.label.key, val as object)"
            mode="single"
          />
        </div>

        <!-- Pricing row: the struck-through prices come from the offer data;
             everything else is author-composed in the `:price` slot. -->
        <div class="offer2-price-row">
          <!-- Both struck prices live in ONE wrapper whose ::after draws the strike
               as a single rule across the pair AND the gap between them. See the
               CSS — a line-through cannot do this job. -->
          <span class="offer2-struck-prices" v-if="data.oldPrice || showPrevious">
            <span class="offer2-old-price" v-if="data.oldPrice" v-text="data.oldPrice"></span>
            <span
              class="offer2-previous-price"
              v-if="showPrevious"
              v-text="data.previousPrice"
            ></span>
          </span>

          <RenderSlot :name="priceSlotName" :children="priceChildren" :editable="editable">
            <span class="offer2-slot-empty">Drop the price line here</span>
          </RenderSlot>

          <!-- Rendered (but hidden) whenever badges are on at all, so selecting a
               card can't shift the row height as the badge appears/disappears. -->
          <span
            v-if="badgeTurnedOn"
            class="offer2-badge"
            :class="{ 'offer2-badge--hidden': !showBadge }"
            :aria-hidden="!showBadge"
          >
            <span class="offer2-tiptap-line">
              <slot name="badge" />
            </span>
          </span>
        </div>

        <div class="offer2-note-row" v-if="hasNoteRow">
          <RenderSlot :name="noteSlotName" :children="noteChildren" :editable="editable">
            <span class="offer2-slot-empty">Drop the note row here</span>
          </RenderSlot>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.offer2-option {
  display: block;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  text-align: start;
  overflow: hidden;
  cursor: pointer;
  border-style: solid;
  border-radius: v-bind('c.borderRadius');
  border-width: v-bind('c.borderWidth');
  background-color: v-bind('c.bgIdle');
  border-color: v-bind('c.borderIdle');
  box-shadow: v-bind('c.shadowIdle');

  // `.is-hover` is the author-preview state class and stays unconditional. The
  // real `:hover` is gated behind a hover-capable pointer so a tapped card
  // doesn't stay highlighted after a tap-driven navigation.
  &.is-hover {
    background-color: v-bind('c.bgHover');
  }

  @media (hover: hover) {
    &:hover {
      background-color: v-bind('c.bgHover');
    }
  }

  // The card is keyboard-focusable (role=radio + tabindex) and a bare div has no
  // UA focus ring, so focus reuses the hover background rather than introducing
  // an outline of its own. Unconditional: `:focus-visible` never matches a tap,
  // so it does not want the hover-capability gate above.
  &:focus-visible {
    outline: none;
    background-color: v-bind('c.bgHover');
  }

  &.active,
  &.is-pressed {
    background-color: v-bind('c.bgPressed');
    border-color: v-bind('c.borderPressed');
    // `box-sizing: border-box` on the base rule is what keeps a thicker pressed
    // border from growing the card and nudging its siblings.
    border-width: v-bind('c.borderWidthPressed');
    box-shadow: v-bind('c.shadowPressed');

    .offer2-best-deal {
      background: v-bind('c.bgHighlightHeaderPressed');
      color: v-bind('c.textColorHighlightLabelPressed');
    }

    .offer2-check-inactive,
    .offer2-check-active circle:first-child {
      background-color: v-bind('c.bgRadioContainerPressed');
      fill: v-bind('c.bgRadioContainerPressed');
    }

    .offer2-title {
      color: v-bind('c.textColorTitlePressed');
    }

    .offer2-struck-prices {
      color: v-bind('c.textColorOldPricePressed');
    }

    .offer2-previous-price {
      color: v-bind('c.textColorPreviousPricePressed');
    }
  }
}

.offer2-tiptap-line {
  min-width: 0;
}

.offer2-best-deal {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2px 16px;
  background: v-bind('c.bgHighlightHeader');
  color: v-bind('c.textColorHighlightLabel');
  font-size: v-bind('c.textSizeHighlightLabel');
  font-weight: 900;
  line-height: 22px;
  text-transform: uppercase;
}

.offer2-content {
  display: flex;
  align-items: center;
  padding: v-bind('c.paddingY') v-bind('c.paddingX');
  gap: v-bind('c.gapColumns');
}

.offer2-check {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}

.offer2-check-inactive {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: v-bind('c.bgRadioContainer');
}

.offer2-check-active {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  circle:first-child {
    fill: v-bind('c.bgRadioContainer');
  }
  circle:last-child {
    fill: v-bind('c.bgRadioFill');
  }
}

.offer2-info {
  padding-block: 0;
  padding-inline: 16px 12px;
  flex: 1 1 100%;
  min-width: 0;
}

.offer2-title {
  font-size: v-bind('c.textSizeTitle');
  font-weight: 900;
  line-height: 32px;
  color: v-bind('c.textColorTitle');
}

.offer2-price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: v-bind('c.gapPriceRow');
  font-weight: 500;
}

.offer2-note-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-block-start: v-bind('c.gapNoteRow');
}

// ONE strike across both prices, drawn as a rule rather than with a
// `line-through`. A propagated line-through is painted per inline descendant, so
// the space between the two prices is skipped however it is produced — a flex gap
// sits outside the inline boxes entirely, and padding on a child is not painted
// over either (verified in the editor). Drawing the line ourselves is the only
// way to get one continuous strike over both prices and the gap.
.offer2-struck-prices {
  position: relative;
  display: inline-flex;
  // Baseline, so the two prices sit on one line of text even at different sizes.
  align-items: baseline;
  column-gap: v-bind('c.gapPriceRow');
  // Gives `currentColor` on the rule below the old-price colour, so the unset
  // default (strike matches the old price) is what it always was.
  // `.offer2-old-price` inherits it rather than restating it.
  color: v-bind('c.textColorOldPrice');
  line-height: 22px;
  // Belt-and-braces: neither price may break internally.
  white-space: nowrap;
}

.offer2-struck-prices::after {
  content: '';
  position: absolute;
  // Edge to edge of the wrapper — this is what covers the gap.
  inset-inline: 0;
  top: 50%;
  transform: translateY(-50%);
  // Fixed hairline: thickness can no longer be derived from the font now that the
  // line is drawn rather than decorated.
  height: 1px;
  background-color: v-bind('c.strikethroughColor');
  pointer-events: none;
}

.offer2-old-price {
  font-size: v-bind('c.textSizeOldPrice');
}

.offer2-previous-price {
  font-size: v-bind('c.textSizePreviousPrice');
  color: v-bind('c.textColorPreviousPrice');
}

.offer2-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  background-color: v-bind('c.badgeBg');
  color: v-bind('c.badgeTextColor');
  font-family: var(--text-label-sm-family);
  font-size: v-bind('c.badgeFontSize');
  font-weight: v-bind('c.badgeFontWeight');
  line-height: var(--text-label-sm-line-height);
  border-radius: v-bind('c.badgeBorderRadius');
  border: v-bind('c.badgeBorderWidth') solid v-bind('c.badgeBorderColor');
  padding: v-bind('c.badgePadding');
  margin: v-bind('c.badgeMargin');
  min-height: v-bind('c.badgeMinHeight');

  &--hidden {
    visibility: hidden;
    pointer-events: none;
    width: 0;
    padding-inline: 0;
    border-inline-width: 0;
    margin-inline: calc(-1 * v-bind('c.gapPriceRow')) 0;
    overflow: hidden;

    .offer2-tiptap-line {
      flex-shrink: 0;
      width: max-content;
    }
  }
}

// Editor-only affordance so an empty slot is discoverable; RenderSlot renders
// this fallback only when nothing is authored, and only in EDITOR mode.
.offer2-slot-empty {
  font-size: 12px;
  font-style: italic;
  opacity: 0.55;
}
</style>
